import { format, subDays, addDays, eachDayOfInterval, isSameDay } from 'date-fns';
import { useRef, useEffect, useMemo } from 'react';
import type { EntriesMap } from '@gg/shared/types';

interface DateStripProps {
  selectedDate: string;
  onSelectDate: (date: string) => void;
  entries?: EntriesMap;
}

const CELL_WIDTH = 64;
const CELL_GAP = 8; // gap-2
const CELL_STEP = CELL_WIDTH + CELL_GAP;

export function DateStrip({ selectedDate, onSelectDate, entries }: DateStripProps) {
  const selectedRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const dates = useMemo(() => {
    const today = new Date();
    return eachDayOfInterval({
      start: subDays(today, 3),
      end: addDays(today, 5),
    });
  }, []);

  // Center selected date inside the strip without yanking the page (#175).
  // Use getBoundingClientRect — offsetLeft is relative to offsetParent (body),
  // not the scroll container, and inflates the target by the strip's page offset.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const target = selectedRef.current;
    if (!container || !target) return;

    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    const delta = tRect.left + tRect.width / 2 - (cRect.left + cRect.width / 2);
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const next = Math.max(0, Math.min(container.scrollLeft + delta, maxScrollLeft));

    container.scrollTo({ left: next, behavior: 'smooth' });
  }, [selectedDate]);

  const selectedDateObj = new Date(selectedDate + 'T00:00:00');
  const selectedIndex = dates.findIndex((d) => isSameDay(d, selectedDateObj));

  return (
    <div ref={scrollContainerRef} className="scrollbar-hidden -mx-4 overflow-x-auto px-4">
      <div className="flex gap-2">
        {dates.map((date) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const isSelected = isSameDay(date, selectedDateObj);
          const hasEntries = entries?.[dateStr] && Object.keys(entries[dateStr]).length > 0;

          return (
            <button
              key={dateStr}
              ref={isSelected ? selectedRef : undefined}
              onClick={() => onSelectDate(dateStr)}
              className={`flex h-[68px] w-[64px] flex-shrink-0 flex-col items-center justify-center gap-1 rounded-xl transition-colors ${
                isSelected ? 'bg-primary text-white' : 'text-content'
              }`}
            >
              <span className="text-[10px] font-normal">{format(date, 'EEE')}</span>
              <span className="text-base font-semibold">{format(date, 'd')}</span>
              <span
                className={`h-1 w-1 rounded-full ${
                  isSelected
                    ? hasEntries
                      ? 'bg-white'
                      : 'bg-transparent'
                    : hasEntries
                      ? 'bg-primary'
                      : 'bg-transparent'
                }`}
              />
            </button>
          );
        })}
      </div>
      <div
        className="relative mt-2 h-[3px] rounded-full bg-surface-secondary"
        style={{ width: `${dates.length * CELL_STEP - CELL_GAP}px` }}
      >
        {selectedIndex >= 0 && (
          <div
            className="absolute h-[3px] w-[35px] rounded-full bg-streak transition-all"
            style={{ left: `${selectedIndex * CELL_STEP + (CELL_WIDTH - 35) / 2}px` }}
          />
        )}
      </div>
    </div>
  );
}
